import { collection, addDoc, doc, getDoc } from 'firebase/firestore';
import { firestore as db } from './firebase';

export interface SubscriptionPlan {
  id: string;
  name: string;
  price: string;
  interval: 'month' | 'year';
  currency: string;
  stripePriceId: string;
  features: string[];
}

// 定义订阅计划
export const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    id: 'free',
    name: 'Free',
    price: '0',
    interval: 'month',
    currency: 'usd',
    stripePriceId: '',
    features: [
      'Basic features',
      'Daily usage limit',
      'Community support',
      'Two-week trial'
    ]
  },
  {
    id: 'monthly',
    name: 'Monthly',
    price: '0.99',
    interval: 'month',
    currency: 'usd',
    stripePriceId: import.meta.env.VITE_STRIPE_PRICE_MONTHLY as string,
    features: [
      'All premium features',
      'No usage limits',
      'Priority support',
      'Early access to new features'
    ]
  },
  {
    id: 'yearly',
    name: 'Yearly',
    price: '9.9',
    interval: 'year',
    currency: 'usd',
    stripePriceId: import.meta.env.VITE_STRIPE_PRICE_YEARLY as string,
    features: [
      'All premium features',
      'No usage limits',
      'Save nearly 17% compared with monthly billing',
      'Priority support',
      'Early access to new features'
    ]
  }
];

// 创建Stripe结账会话
export const createCheckoutSession = async (
  userId: string, 
  priceId: string,
  successUrl = window.location.origin + '/subscription?success=true',
  cancelUrl = window.location.origin + '/pricing'
) => {
  try {
    // 创建结账会话文档
    const docRef = await addDoc(collection(db, 'users', userId, 'checkout_sessions'), {
      price: priceId,
      success_url: successUrl,
      cancel_url: cancelUrl,
      mode: 'subscription',
    });

    // 等待Cloud Functions处理并更新文档
    let checkoutSessionRef;
    const maxTries = 10;
    let tries = 0;
    
    while (tries < maxTries) {
      checkoutSessionRef = await getDoc(doc(db, 'users', userId, 'checkout_sessions', docRef.id));
      const checkoutSession = checkoutSessionRef.data();
      
      if (checkoutSession && checkoutSession.url) {
        // 重定向到Stripe托管的结账页面
        window.location.assign(checkoutSession.url);
        return;
      } else if (checkoutSession && checkoutSession.error) {
        throw new Error(`Failed to create checkout session: ${checkoutSession.error.message}`);
      }
      
      // 等待一段时间再重试
      await new Promise(resolve => setTimeout(resolve, 300));
      tries++;
    }
    
    throw new Error('Timed out creating checkout session');
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
};

// 获取用户的订阅信息
export const getUserSubscription = async (userId: string) => {
  try {
    const subscriptionRef = doc(db, 'users', userId, 'subscriptions', 'active');
    const subscriptionDoc = await getDoc(subscriptionRef);
    
    if (subscriptionDoc.exists()) {
      return subscriptionDoc.data();
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching user subscription:', error);
    return null;
  }
};

// 检查用户是否有订阅或处于试用期
export const checkUserSubscription = async (userId: string) => {
  try {
    const subscription = await getUserSubscription(userId);
    
    if (!subscription) {
      return {
        isSubscribed: false,
        isTrialing: false,
        status: 'inactive',
      };
    }
    
    const isActive = ['active', 'trialing'].includes(subscription.status);
    const isTrialing = subscription.status === 'trialing';
    
    return {
      isSubscribed: isActive,
      isTrialing,
      status: subscription.status,
      plan: subscription.planId,
      trialEnd: subscription.trial_end?.toDate(),
      currentPeriodEnd: subscription.current_period_end?.toDate(),
    };
  } catch (error) {
    console.error('Error checking user subscription:', error);
    return {
      isSubscribed: false,
      isTrialing: false,
      status: 'error',
    };
  }
}; 
